import { isAfter, parseISO, isValid } from 'date-fns'
import { notifyBugsnag } from 'js/lib/bugsnag'

const COMPONENT_SELECTOR = '.js-c-form-display'

document.querySelectorAll(COMPONENT_SELECTOR).forEach(element => {
  try {
    const data = JSON.parse(element.getAttribute('data-dfn') || '{}')
    initialize(element, data)
  } catch (error) {
    // Don't cause the page to crash if it can't parse the json
    notifyBugsnag(error)
  }
})

function initialize(element, data) {
  const formElement = element.closest('form')
  if (!formElement) return

  formElement.addEventListener('change', function (event) {
    if (!event.target.matches('input, select, textarea')) return
    handleFormStateChange(element, data)
  })

  handleFormStateChange(element, data)
}

function handleFormStateChange(element, data) {
  // data.for selects values from input names (rails helpers makes it easier to select the input names)
  const forValueContent = getInputValues(getInputsByNames(data.for))
  // data.select selects values from css selectors making it a bit more flexible but more manual to set it up
  const selectValueContent = getContentBySelectors(data.select)

  const result = calculateConditions(data.when, { ...forValueContent, ...selectValueContent })

  if (result) {
    element.style.display = ''
    element.setAttribute('aria-hidden', 'false')
  } else {
    element.style.display = 'none'
    element.setAttribute('aria-hidden', 'true')
  }
}

function calculateConditions(when, inputValues) {
  if (!when || typeof when !== 'object') return false
  const [operator, value] = Object.entries(when)[0] || []

  // We always assume value is an array
  const values = Array.isArray(value) ? value : [value]

  switch (operator) {
    case 'eq':
      return compareEq(inputValues, values)
    case 'gteq':
      return compareGteq(inputValues, values)
    case 'not_eq':
      return compareNot(inputValues, values)
    case 'in':
      return compareIn(inputValues, values)
    // Add more cases for other operators as needed
    default:
      return false
  }
}

function compareEq(inputValues, values) {
  return Object.values(inputValues)
    .flat()
    .some(inputValue => values.some(v => String(inputValue).toLowerCase() === String(v).toLowerCase()))
}

function compareNot(inputValues, values) {
  return Object.values(inputValues)
    .flat()
    .some(inputValue => !values.map(v => String(v).toLowerCase()).includes(String(inputValue).toLowerCase()))
}

function compareIn(inputValues, values) {
  return Object.values(inputValues)
    .flat()
    .some(inputValue =>
      values.map(v => String(v).toLowerCase())
        .some(val => String(inputValue).toLowerCase().includes(val))
    )
}

function compareGteq(inputValues, values) {
  return Object.values(inputValues)
    .flat()
    .some(inputValue => {
      const inputType = getInputValueType(inputValue)
      if (inputType === 'string') return false

      return values.some(v => {
        const valueType = getInputValueType(v)

        if (inputType === 'number' && valueType === inputType) {
          return Number(inputValue) >= Number(v)
        }

        if (inputType === 'date' && valueType === inputType) {
          return isAfter(parseISO(inputValue), parseISO(v)) || inputValue === v
        }

        return false
      })
    })
}

function getInputValues(inputs) {
  const inputValues = {}

  inputs.forEach(input => {
    const name = input.name
    if (!inputValues[name]) inputValues[name] = []

    if (input.type === 'checkbox' || input.type === 'radio') {
      if (input.checked) inputValues[name].push(input.value)
    } else if (input.tagName === 'SELECT' && input.multiple) {
      // For multi-select, collect all selected options
      Array.from(input.selectedOptions).forEach(option => {
        inputValues[name].push(option.value)
      })
    } else {
      inputValues[name].push(input.value)
    }
  })

  return inputValues
}

function getInputsByNames(names) {
  if (!Array.isArray(names)) return []
  return names.flatMap(name =>
    Array.from(document.querySelectorAll(`input[name="${name}"], select[name="${name}"], textarea[name="${name}"]`))
  )
}

function getInputValueType(value) {
  const trimmedValue = value == null ? '' : String(value).trim()

  if (trimmedValue === '') return 'undefined'
  if (isFinite(trimmedValue)) return 'number'

  const d = parseISO(trimmedValue)
  if (d instanceof Date && isValid(d)) return 'date'

  return 'string'
}

// When fetching content from a selector, we do different things depending on what the selector
// points to:
//    - if the seector matches to an attribute (e.g. [data-foo], [type="text"])
//      then we will fetch that attribute.
//    - if the selector points to an input, we get the value of that input
//    - if the selector points to any other element, we get the value of the text content (trimmed)
function getContentBySelectors(selectors) {
  if (!Array.isArray(selectors) || selectors.length === 0) return []

  const content = {}

  for (const selector of selectors) {
    // Loop through the different ways of selecting content and get the first not undefined value

    const attrContent = getAttributeContent(selector)
    if (attrContent.length >= 1) {
      content[selector] = attrContent
      continue
    }

    const inputContent = getInputContent(selector)
    if (inputContent.length >= 1) {
      content[selector] = inputContent
      continue
    }

    const textContent = getTextContent(selector)
    if (textContent.length >= 1) {
      content[selector] = textContent
      continue
    }
  }

  return content
}

function getAttributeContent(selector) {
  const content = []
  // Attribute selector: e.g. [data-foo], [type="text"]
  const attrMatch = selector.match(/^(.+)?\[(.+?)(=["']?(.*?)["']?)?\]$/)
  if (!attrMatch) return content

  const baseSelector = attrMatch[1] ? attrMatch[1].trim() : ''
  const attrName = attrMatch[2]
  const fullSelector = baseSelector ? `${baseSelector}[${attrName}]` : `[${attrName}]`
  const els = document.querySelectorAll(fullSelector)

  els.forEach(el => {
    if (el && el.hasAttribute(attrName)) {
      content.push(el.getAttribute(attrName))
    }
  })

  return content
}

function getInputContent(selector) {
  const content = []
  const els = document.querySelectorAll(selector)

  els.forEach(el => {
    if (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA') {
      content.push(el.value)
    }
  })

  return content
}

function getTextContent(selector) {
  const content = []
  const els = document.querySelectorAll(selector)

  els.forEach(el => {
    if (el.textContent != null) {
      content.push(el.textContent.trim())
    }
  })

  return content
}
